<template>
    <b-container fluid class="mt-3">
        <div v-if="currentClient == null" class="text-center">
            <b-spinner label="Spinning"></b-spinner>
        </div>
        <b-row v-if="currentClient">
            <b-col md="6">
                <h4>Client Instance</h4>

                <b-form @submit="onSubmit">
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Name" label-for="clientName" class="required">
                                <b-form-input id="clientName" name="clientName" v-model="currentClient.name"
                                              required></b-form-input>
                            </b-form-group>
                        </b-col>

                        <b-col md="6">
                            <b-form-group label="Icon" label-for="iconName" class="required">
                                <b-input-group>
                                    <b-form-input id="iconName" name="iconName" v-model="currentClient.shortName"
                                                  maxlength="3" required></b-form-input>
                                    <b-form-input id="iconColor" type="color" :value="currentClient.color"
                                                  @change="updateColor($event)"></b-form-input>
                                    <b-avatar :text="currentClient.shortName" :variant="currentClient.color.startsWith('#') ? '' : currentClient.color"
                                              :style="currentClient.color.startsWith('#') ? `color: #fff; background: ${currentClient.color}` : ''"
                                              class="ml-3"></b-avatar>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group label="Tenant" label-for="tenant" class="required">
                        <b-form-input id="tenant" name="tenant" v-model="currentClient.settings.tenant"
                                      trim required></b-form-input>
                    </b-form-group>

                    <!-- Url details -->
                    <b-form-group>
                        <label for="partnerUrl">
                            <b-link v-b-toggle="'url-details'"
                                    class="text-primary" size="sm">
                                <b-icon class="when-opened" icon="chevron-down"></b-icon>
                                <b-icon class="when-closed" icon="chevron-right"></b-icon>
                            </b-link>
                            Tenant URL details
                            <b-link id="tenantHelp">
                                <b-icon icon="question-circle" variant="primary"></b-icon>
                            </b-link>
                            <b-popover target="tenantHelp" triggers="focus" placement="top">
                                If different than<br>
                                <b>WS :</b> https://<b><i>tenant</i></b>.partneringplace.com<br>
                                <b>Url :</b> https://<b><i>tenant</i></b>.partneringplace.com
                            </b-popover>
                        </label>
                    <b-collapse id="url-details">
                        <b-card bg-variant="light" class="mb-2">
                            <b-input-group prepend="WS Url">
                                <b-form-input id="partnerUrl" name="partnerUrl" v-model="currentClient.settings.partnerUrl" trim></b-form-input>
                                <b-input-group-prepend is-text>Url</b-input-group-prepend>
                                <b-form-input id="url" name="url" v-model="currentClient.settings.url" trim></b-form-input>
                            </b-input-group>
                        </b-card>
                    </b-collapse>
                    </b-form-group>

                    <!-- Partner -->
                    <b-form-group label="Technical User" label-for="partnerUsername">
                        <b-row>
                            <b-col md="6">
                                <!-- Username -->
                                <b-input-group prepend="Username">
                                    <b-form-input id="partnerUsername" name="partnerUsername"
                                                  v-model="currentClient.settings.partnerUsername"></b-form-input>
                                </b-input-group>
                            </b-col>
                            <b-col md="6">
                                <!-- Password -->
                                <b-input-group prepend="Password" v-if="changePassword || isNewClient" >
                                    <b-form-input id="partnerPassword" name="partnerPassword"
                                                  v-model="currentClient.settings.partnerPassword"
                                                  type="password"></b-form-input>
                                    <b-input-group-append is-text>
                                        <b-link @click="changePassword = !changePassword" class="text-secondary">
                                            <b-icon icon="x-circle"></b-icon>
                                        </b-link>
                                    </b-input-group-append>
                                </b-input-group>

                                <b-input-group prepend="Password" v-if="!changePassword && !isNewClient">
                                    <b-form-input id="partnerPassword" name="partnerPassword"
                                                  placeholder="Change password" disabled
                                                  v-model="currentClient.settings.partnerPassword"
                                                  type="password"></b-form-input>
                                    <b-input-group-append is-text>
                                        <b-link @click="changePassword = !changePassword" class="text-primary">
                                            <b-icon icon="pencil"></b-icon>
                                        </b-link>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                        </b-row>
                    </b-form-group>

                    <!-- Deal -->
                    <b-form-group>
                        <label for="lsUsername">
                            <b-link v-b-toggle="'ls-details'"
                                    class="text-primary" size="sm">
                                <b-icon class="when-opened" icon="chevron-down"></b-icon>
                                <b-icon class="when-closed" icon="chevron-right"></b-icon>
                            </b-link>
                            Deal
                            <b-link id="lsHelp">
                                <b-icon icon="question-circle" variant="primary"></b-icon>
                            </b-link>
                            <b-popover target="lsHelp" triggers="focus" placement="top">
                                If Technical User is different than Partner Username/Password
                            </b-popover>
                        </label>
                        <b-collapse id="ls-details">
                            <b-card bg-variant="light" class="mb-2">
                                <b-row>
                                    <b-col md="6">
                                        <!-- Username -->
                                        <b-input-group prepend="Username">
                                            <b-form-input id="lsUsername" name="lsUsername"
                                                          v-model="currentClient.settings.lsUsername"></b-form-input>
                                        </b-input-group>
                                    </b-col>
                                    <b-col md="6">
                                        <!-- Password -->
                                        <b-input-group prepend="Password" v-if="changeLsPassword || isNewClient" >
                                            <b-form-input id="lsPassword" name="lsPassword"
                                                          v-model="currentClient.settings.lsPassword"
                                                          type="password"></b-form-input>
                                            <b-input-group-append is-text>
                                                <b-link @click="changeLsPassword = !changeLsPassword" class="text-secondary">
                                                    <b-icon icon="x-circle"></b-icon>
                                                </b-link>
                                            </b-input-group-append>
                                        </b-input-group>

                                        <b-input-group prepend="Password" v-if="!changeLsPassword && !isNewClient">
                                            <b-form-input id="lsPassword" name="lsPassword"
                                                          placeholder="Change password" disabled
                                                          v-model="currentClient.settings.lsPassword"
                                                          type="password"></b-form-input>
                                            <b-input-group-append is-text>
                                                <b-link @click="changeLsPassword = !changeLsPassword" class="text-primary">
                                                    <b-icon icon="pencil"></b-icon>
                                                </b-link>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-collapse>
                    </b-form-group>

                    <b-form-group v-if="isAdmin">
                        <label for="jsonPrefix">
                            <b-link v-b-toggle="'admin-details'"
                                    class="text-primary" size="sm">
                                <b-icon class="when-opened" icon="chevron-down"></b-icon>
                                <b-icon class="when-closed" icon="chevron-right"></b-icon>
                            </b-link>
                            Admin
                        </label>
                        <b-collapse id="admin-details">
                            <b-card bg-variant="light" class="mb-2">
                                <b-row>
                                    <b-col md="6">
                                        <b-form-group label="IAPe http json prefix" label-for="jsonPrefix">
                                            <b-form-input id="jsonPrefix" name="jsonPrefix"
                                                          v-model="currentClient.settings.jsonPrefix"></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group label="Cookie (dev)" label-for="devCookie">
                                            <b-form-input id="devCookie" name="devCookie"
                                                          v-model="currentClient.settings.devCookie"></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-collapse>
                    </b-form-group>

                    <b-row class="mt-4 mb-4">
                        <b-col md="6" class="text-left">
                            <b-button squared variant="outline-primary" class="mr-2" :to="{ name: 'Clients'}">
                                Cancel
                            </b-button>
                        </b-col>

                        <b-col md="6" class="text-right">
                            <b-button squared v-if="isNewClient" type="submit" variant="primary">
                                <b-icon icon="check"></b-icon>
                                Create
                            </b-button>

                            <b-button squared v-if="!isNewClient" type="submit" variant="primary" class="mr-2">
                                <b-icon icon="check"></b-icon>
                                Update
                            </b-button>
                            <b-button squared v-if="isAdmin && !isNewClient" @click="deleteClient" variant="danger">
                                <b-icon icon="x"></b-icon>
                                Delete
                            </b-button>
                        </b-col>
                    </b-row>

                </b-form>
            </b-col>

            <b-col md="6" v-if="!isNewClient">
                <b-row class="mb-3">
                    <b-col cols="auto" class="mr-auto"><h4>Portals</h4></b-col>
                    <b-col cols="auto">
                        <b-link :to="{ name: 'ConfNewPortal', params: { isNewPortal: true, client: currentClient } }"
                                class="h4 text-primary">
                            <b-icon icon="plus-circle"></b-icon>
                        </b-link>
                    </b-col>
                </b-row>
                <b-list-group v-if="portals != null">
                    <b-list-group-item class="text-center" v-if=" portals.length === 0">
                        No portal found
                    </b-list-group-item>
                    <b-list-group-item v-for="portal in portals"
                                       :key="portal.id"
                                       :to="{ name: 'ConfPortal', params: { name: portal.name } }">
                        {{ portal.name }}
                    </b-list-group-item>
                </b-list-group>

                <div v-if="!isNewClient && portals == null" class="text-center">
                    <b-spinner label="Spinning"></b-spinner>
                </div>
            </b-col>
        </b-row>

    </b-container>
</template>

<script>
    import ClientDataService from "../../services/ClientDataService";
    import PortalDataService from "../../services/PortalDataService";
    import StringUtils from "@/services/utils/StringUtils";

    export default {
        name: "Client",
        mixins: [StringUtils],
        props: {
            isNewClient: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                currentClient: null,
                portals: null,
                connectionStatus: null,
                showConnectionStatus: false,
                changePassword: false,
                changeLsPassword: false
            };
        },
        mounted() {
            if (this.isNewClient) {
                this.currentClient = {
                    settings: {},
                    color: "#008bcb"
                };
            } else {
                this.getClient(this.$route.params.id);
            }
        },
        computed: {
            isAdmin() {
                return this.$store.getters.isAdmin;
            }
        },
        methods: {
            getClient(id) {
                ClientDataService.get(id)
                    .then(response => {
                        this.currentClient = response.data;
                        this.getPortalByClient();
                    })
                    .catch(error => {
                        console.log(error);
                        this.$router.push({name: "Clients"});
                    });
            },
            onSubmit(evt) {
                if (this.isNewClient) {
                    this.saveClient();
                } else {
                    this.updateClient();
                }
                evt.preventDefault();
            },
            saveClient() {
                this.$bvModal.show('loading-modal');
                ClientDataService.create(this.currentClient)
                    .then(response => {
                        this.$parent.displaySuccessMessage('The client was created successfully.');
                        this.$router.push({name: "Client", params: {id: response.data.id}});
                    })
                    .catch(error => {
                        console.log(error);
                        this.$parent.displayErrorMessage('An error occurred while trying to create the client.');
                    });
            },
            updateClient() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to update the client ?', {
                    title: 'Please Confirm',
                    okVariant: 'success',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            ClientDataService.update(this.currentClient.id, this.currentClient, this.changePassword, this.changeLsPassword)
                                .then(response => {
                                    this.currentClient = response.data;
                                    this.changePassword = false;
                                    this.changeLsPassword = false;
                                    this.$parent.displaySuccessMessage('The client was updated successfully.');
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage('An error occurred while trying to updated the client.');
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            deleteClient() {
                this.$bvModal.msgBoxConfirm('Are you sure you want to delete the client ?', {
                    title: 'Please Confirm',
                    okVariant: 'danger',
                    okTitle: 'YES',
                    cancelTitle: 'NO',
                    hideHeaderClose: false,
                })
                    .then(value => {
                        if (value) {
                            this.$bvModal.show('loading-modal');
                            ClientDataService.delete(this.currentClient.id)
                                .then(response => {
                                    this.currentClient = response.data;
                                    this.$parent.displaySuccessMessage('The client was deleted successfully.');
                                    this.$router.push({name: "Clients"});
                                })
                                .catch(error => {
                                    console.log(error);
                                    this.$parent.displayErrorMessage('An error occurred while trying to delete the client.');
                                });
                        }
                    })
                    .catch(error => console.log(error));
            },
            getPortalByClient() {
                PortalDataService.findByClient(this.currentClient.id)
                    .then(response => {
                        this.portals = response.data
                    })
                    .catch(error => console.log(error))
            },
            updateColor(e) {
                this.currentClient.color = e;
            }
        }
    };
</script>

<style scoped>

</style>